import React from "react";

import { useLocalStorage } from "./LocalStorageContext";

interface UserPreferencesContextInterface {
  getListRowPerPage: (listName: string) => number | undefined;
  setListRowPerPage: (listName: string, rowsPerPage: number) => void;
}

const UserPreferencesContext = React.createContext<UserPreferencesContextInterface>({
  getListRowPerPage: () => undefined,
  setListRowPerPage: () => {},
});

interface Props {
  children: React.ReactNode;
}

export const UserPreferencesProvider: React.FC<Props> = ({ children }) => {
  const { getItem, setItem } = useLocalStorage();

  const [listRowPerPages, setListRowPerPages] = React.useState<Record<string, number>>({});

  const getListRowPerPage = React.useCallback((listName: string) => listRowPerPages[listName], [listRowPerPages]);

  // Load data from localStorage on mount
  React.useEffect(() => {
    const fetch = async () => {
      const stored = await getItem<Record<string, number>>("listRowPerPages");
      if (stored) {
        setListRowPerPages(stored);
      }
    };

    fetch();
  }, [getItem]);

  const setListRowPerPage = React.useCallback(
    (listName: string, rowsPerPage: number) => {
      setListRowPerPages((prev) => {
        const next = {
          ...prev,
          [listName]: rowsPerPage,
        };

        // Persist the *new* object, not the old one
        setItem<Record<string, number>>("listRowPerPages", next);

        return next;
      });
    },
    [setItem],
  );

  return (
    <UserPreferencesContext.Provider
      value={{
        getListRowPerPage,
        setListRowPerPage,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};
